import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Key, CheckCircle2, XCircle, Loader2,
  Eye, EyeOff, Copy,
  FileJson, FileSpreadsheet, FileText, Calendar,
  ChevronDown, ChevronUp, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApiInterfacesPanel({ orgId }) {
  const { isDark } = useTheme();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [showApiKey, setShowApiKey] = useState(false);
  // Default to opening the Native OL API section since the legacy MSTW
  // WordPress Integration card was removed in Session 19 (May 12, 2026).
  // All OL↔WP bridge configuration is now exclusively at
  // Tech Settings → Site Linking; the standalone MSTW toggle here had no
  // remaining purpose and was confusing operators.
  const [expandedSection, setExpandedSection] = useState('native');

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg({ text: '', type: '' }), 4000); };

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/api-interfaces/config/${orgId}`);
      setConfig(data);
    } catch (err) { console.error('Failed to load API config:', err); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const toggleFormat = async (format, currentEnabled) => {
    try {
      await axios.put(`${API_URL}/api/api-interfaces/config/${orgId}`, {
        interface_type: format, config: { enabled: !currentEnabled },
      });
      fetchConfig();
      showMsg(`${format.toUpperCase()} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) { showMsg('Failed to toggle', 'error'); }
  };

  const generateNativeKey = async () => {
    try {
      await axios.post(`${API_URL}/api/api-interfaces/native-api/generate-key/${orgId}`);
      showMsg('API key generated. Copy it now — it won\'t be shown again.');
      setShowApiKey(true);
      fetchConfig();
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to generate key', 'error'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => showMsg('Copied to clipboard'));
  };

  const toggleSection = (key) => setExpandedSection(expandedSection === key ? '' : key);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  const cardClass = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const textClass = isDark ? 'text-white' : 'text-slate-900';
  const subText = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-4" data-testid="api-interfaces-panel">
      {msg.text && (
        <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${msg.type === 'error' ? 'bg-red-500/10 border border-red-500/50 text-red-400' : 'bg-green-500/10 border border-green-500/50 text-green-400'}`}>
          {msg.type === 'error' ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}{msg.text}
        </div>
      )}

      {/* MSTW WordPress Integration card removed in Session 19 (May 12 2026).
          The OL↔WP bridge is fully configured via Tech Settings → Site Linking;
          this disabled-by-default toggle had no remaining product purpose and
          was confusing for operators since the live integration is healthy. */}

      {/* Native OL API */}
      <Card className={cardClass} data-testid="native-api-config-card">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('native')}>
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 text-lg ${textClass}`}>
              <Zap className="h-5 w-5 text-purple-500" />
              Native OL API
              {config?.native_api?.enabled && <Badge className="bg-purple-500/20 text-purple-400 ml-2">Active</Badge>}
            </CardTitle>
            {expandedSection === 'native' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
          <p className={`text-sm ${subText}`}>External API access for third-party integrations</p>
        </CardHeader>
        {expandedSection === 'native' && (
          <CardContent className="space-y-4">
            <p className={`text-sm ${subText}`}>
              Generate an API key to allow external applications to read your organization's schedules, rosters, standings, and more.
              Include the key in requests as the <code className="px-1 py-0.5 rounded bg-slate-700/50 text-xs">X-OL-API-KEY</code> header.
            </p>

            {config?.native_api?.api_key ? (
              <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-purple-50 border-purple-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Key className="h-4 w-4 text-purple-500" />
                  <span className={`text-sm font-medium ${textClass}`}>API Key</span>
                  {config.native_api.generated_at && (
                    <span className={`text-xs ${subText}`}>Generated: {new Date(config.native_api.generated_at).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <code className={`text-xs flex-1 p-2 rounded ${isDark ? 'bg-slate-800' : 'bg-white'} ${textClass}`}>
                    {showApiKey ? config.native_api.api_key : config.native_api.api_key_masked || '****'}
                  </code>
                  <button onClick={() => setShowApiKey(!showApiKey)} className={subText}>
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  {showApiKey && (
                    <button onClick={() => copyToClipboard(config.native_api.api_key)} className={subText}>
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className={`text-sm ${subText}`}>No API key generated yet.</p>
            )}

            <div className="flex gap-2">
              <Button onClick={generateNativeKey} variant="outline" className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="generate-native-key-btn">
                <Key className="h-4 w-4 mr-1" />{config?.native_api?.api_key ? 'Regenerate Key' : 'Generate API Key'}
              </Button>
            </div>

            {config?.native_api?.api_key && (
              <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/20 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                <p className={`text-xs font-medium mb-1 ${textClass}`}>Available Endpoints:</p>
                <div className={`text-xs space-y-0.5 ${subText}`}>
                  <p><code className="text-green-400">GET</code> /api/native/teams</p>
                  <p><code className="text-green-400">GET</code> /api/native/schedules</p>
                  <p><code className="text-green-400">GET</code> /api/native/rosters</p>
                  <p><code className="text-green-400">GET</code> /api/native/standings</p>
                  <p><code className="text-green-400">GET</code> /api/native/venues</p>
                  <p><code className="text-green-400">GET</code> /api/native/results</p>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Data Formats */}
      <Card className={cardClass} data-testid="data-formats-card">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('formats')}>
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 text-lg ${textClass}`}>
              <FileJson className="h-5 w-5 text-green-500" />
              Data Formats
            </CardTitle>
            {expandedSection === 'formats' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
          <p className={`text-sm ${subText}`}>Enable/disable import and export formats</p>
        </CardHeader>
        {expandedSection === 'formats' && (
          <CardContent>
            <div className="space-y-3">
              {[
                { key: 'csv', label: 'CSV', icon: FileText, desc: 'Comma-separated values for spreadsheets' },
                { key: 'excel', label: 'Excel', icon: FileSpreadsheet, desc: 'Microsoft Excel format (.xlsx)' },
                { key: 'json', label: 'JSON', icon: FileJson, desc: 'Structured data for APIs and developers' },
                { key: 'google_calendar', label: 'Google Calendar', icon: Calendar, desc: 'ICS feed and Google Calendar links' },
                { key: 'google_sheets', label: 'Google Sheets', icon: FileSpreadsheet, desc: 'Bidirectional Google Sheets sync (coming soon)' },
              ].map(fmt => (
                <div key={fmt.key} className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? 'bg-slate-700/20 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                  data-testid={`format-${fmt.key}`}>
                  <div className="flex items-center gap-3">
                    <fmt.icon className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                    <div>
                      <p className={`text-sm font-medium ${textClass}`}>{fmt.label}</p>
                      <p className={`text-xs ${subText}`}>{fmt.desc}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleFormat(fmt.key, config?.[fmt.key]?.enabled)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${config?.[fmt.key]?.enabled ? 'bg-green-500' : isDark ? 'bg-slate-600' : 'bg-slate-300'}`}
                    data-testid={`format-toggle-${fmt.key}`}
                    disabled={fmt.key === 'google_sheets'}
                  >
                    <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform"
                      style={{ left: config?.[fmt.key]?.enabled ? '22px' : '2px' }} />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
